import React, { useState, useRef, useEffect } from 'react';

const fmtTime = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return m + ':' + String(sec).padStart(2, '0');
};

export default function DialogRecorder({ userProfile, onClose, onAnalyze, variant = 'dialog' }) {
  const isVoice = variant === 'voice';
  const isPaid = ['paid', 'admin', 'moderator'].includes(userProfile && userProfile.role);
  const maxSec = isVoice ? 30 : (isPaid ? 1800 : 120);
  const [phase, setPhase] = useState('idle');
  const [elapsed, setElapsed] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [err, setErr] = useState('');
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const blobRef = useRef(null);
  const wakeRef = useRef(null);
  const fileInputRef = useRef(null);

  const cleanup = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (wakeRef.current) { try { wakeRef.current.release(); } catch (_) {} wakeRef.current = null; }
  };

  useEffect(() => () => { cleanup(); if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== 'inactive') mediaRef.current.stop();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const start = async () => {
    setErr('');
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || typeof MediaRecorder === 'undefined') {
      setErr('Ваш браузер не поддерживает запись с микрофона. Попробуйте Chrome.');
      setPhase('error');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/webm')) ? 'audio/webm' : '';
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: (mr.mimeType || 'audio/webm') });
        blobRef.current = blob;
        setAudioUrl(URL.createObjectURL(blob));
        setPhase('done');
        cleanup();
      };
      mediaRef.current = mr;
      mr.start();
      try { if (navigator.wakeLock) { wakeRef.current = await navigator.wakeLock.request('screen'); } } catch (_) {}
      setPhase('recording');
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((prev) => {
          const n = prev + 1;
          if (n >= maxSec) stop();
          return n;
        });
      }, 1000);
    } catch (e) {
      setErr('Не удалось получить доступ к микрофону. Разрешите доступ в настройках браузера.');
      setPhase('error');
    }
  };

  const reset = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null); blobRef.current = null; setElapsed(0); setPhase('idle'); setErr('');
  };

  const pickFile = () => {
    if (fileInputRef.current) { fileInputRef.current.value = ''; fileInputRef.current.click(); }
  };

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setErr('');
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    const url = URL.createObjectURL(file);
    blobRef.current = file;
    setAudioUrl(url);
    setElapsed(0);
    try {
      const a = new Audio();
      a.preload = 'metadata';
      a.onloadedmetadata = () => {
        const d = (a.duration && isFinite(a.duration)) ? Math.round(a.duration) : 0;
        setElapsed(d);
      };
      a.src = url;
    } catch (_) {}
    setPhase('done');
  };

  return (
    <div className="song-modal" onClick={onClose}>
      <div className="song-modal-card rec-card" onClick={(e) => e.stopPropagation()}>
        <div className="song-modal-title">{isVoice ? 'Образец голоса' : 'Запись разговора'}</div>
        {!isVoice && (<p className="rec-warn">Предупредите собеседника о записи. Вы отвечаете за соблюдение местных правил записи разговоров.</p>)}
        <p className="rec-hint">{isVoice
          ? 'Скажите 2–3 предложения обычным голосом — по этому образцу ИИ будет узнавать вас в разборах. До 30 секунд.'
          : ('Совет: в начале записи назовите своё имя, дату и причину встречи — это поможет разбору. На время записи не давайте экрану гаснуть. Лимит: ' + fmtTime(maxSec) + ' (' + (isPaid ? 'платный' : 'бесплатный') + ').')}</p>

        {phase === 'recording' ? (
          <div className="rec-live"><span className="rec-dot" /><span className="rec-time">{fmtTime(elapsed)} / {fmtTime(maxSec)}</span></div>
        ) : null}
        {phase === 'done' && audioUrl ? (
          <audio className="rec-audio" src={audioUrl} controls />
        ) : null}
        {err ? <p className="rec-err">{err}</p> : null}

        <div className="song-modal-actions">
          {(phase === 'idle' || phase === 'error') ? (
            <button type="button" className="song-modal-cancel" onClick={onClose}>Отмена</button>
          ) : null}
          {((phase === 'idle' || phase === 'error') && !isVoice) ? (
            <button type="button" className="btn-std song-modal-file" onClick={pickFile}>Из файла</button>
          ) : null}
          {(phase === 'idle' || phase === 'error') ? (
            <button type="button" className="btn-std song-modal-go" onClick={start}>● Запись</button>
          ) : null}
          {phase === 'recording' ? (
            <button type="button" className="btn-std song-modal-go" onClick={stop}>■ Стоп</button>
          ) : null}
          {phase === 'done' ? (
            <button type="button" className="song-modal-cancel" onClick={reset}>Заново</button>
          ) : null}
          {phase === 'done' ? (
            <button type="button" className="btn-std song-modal-go" onClick={() => onAnalyze && onAnalyze(blobRef.current, elapsed)}>{isVoice ? 'Сохранить' : 'Анализировать'}</button>
          ) : null}
        </div>
        <input ref={fileInputRef} type="file" accept="audio/*" style={{ display: 'none' }} onChange={onFile} />
      </div>
    </div>
  );
}
